import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Modal,
    ModalContent,
    ModalOverlay,
    Progress,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    VStack,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import {
    MdClose,
    MdGraphicEq,
    MdPause,
    MdPlayArrow,
    MdRemove,
    MdAdd,
    MdReplay,
} from "react-icons/md";
import {INSTRUMENTS, getAudioContext, playHit} from "./ptAudio";
import {formatDuration, sequenceDuration, sequenceInstruments, typeMeta} from "./SampleSequences";

const clock = (seconds) => {
    const s = Math.max(0, Math.round(seconds));
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const MIN_BPM = 40;
const MAX_BPM = 200;

// Interactive step-sequencer player for a sample PT sound sequence.
// It steps through the pattern one beat at a time, triggering the synthesised
// voices and lighting up the visual grid. Tempo and repetitions can be tuned
// live while it plays.
function SequencePlayer({isOpen, onClose, sequence}) {
    const meta = sequence ? typeMeta(sequence.type) : {label: "", color: "teal"};
    const rows = useMemo(() => (sequence ? sequenceInstruments(sequence) : []), [sequence]);
    const steps = sequence?.steps || 8;

    const [bpm, setBpm] = useState(sequence?.bpm || 100);
    const [reps, setReps] = useState(sequence?.defaultReps || 8);
    const [running, setRunning] = useState(false);
    const [finished, setFinished] = useState(false);
    const [activeStep, setActiveStep] = useState(-1);
    const [cycle, setCycle] = useState(0);

    const stepRef = useRef(0);
    const cycleRef = useRef(0);

    // Reset all playback state when a different sequence is opened.
    useEffect(() => {
        if (isOpen && sequence) {
            setBpm(sequence.bpm);
            setReps(sequence.defaultReps);
            setRunning(false);
            setFinished(false);
            setActiveStep(-1);
            setCycle(0);
            stepRef.current = 0;
            cycleRef.current = 0;
        }
    }, [isOpen, sequence?.id]);

    // The beat clock. Recreated whenever tempo / reps change so edits apply live.
    useEffect(() => {
        if (!isOpen || !running || !sequence) return undefined;
        const intervalMs = 60000 / bpm;
        const tick = () => {
            const step = stepRef.current;
            rows.forEach((inst) => {
                const value = sequence.pattern[inst]?.[step] || 0;
                if (value > 0) playHit(inst, {accent: value === 2});
            });
            setActiveStep(step);

            let nextStep = step + 1;
            if (nextStep >= steps) {
                nextStep = 0;
                const nextCycle = cycleRef.current + 1;
                if (nextCycle >= reps) {
                    setRunning(false);
                    setFinished(true);
                    setActiveStep(-1);
                    stepRef.current = 0;
                    cycleRef.current = 0;
                    setCycle(0);
                    return;
                }
                cycleRef.current = nextCycle;
                setCycle(nextCycle);
            }
            stepRef.current = nextStep;
        };
        // Fire the first beat immediately, then keep time.
        tick();
        const id = setInterval(tick, intervalMs);
        return () => clearInterval(id);
    }, [isOpen, running, bpm, reps, steps, rows, sequence]);

    const start = useCallback(() => {
        getAudioContext(); // unlock audio from this click
        if (finished) {
            stepRef.current = 0;
            cycleRef.current = 0;
            setCycle(0);
            setFinished(false);
        }
        setRunning(true);
    }, [finished]);

    const pause = useCallback(() => setRunning(false), []);

    const restart = useCallback(() => {
        stepRef.current = 0;
        cycleRef.current = 0;
        setCycle(0);
        setActiveStep(-1);
        setFinished(false);
        setRunning(true);
    }, []);

    const handleClose = () => {
        setRunning(false);
        onClose();
    };

    if (!sequence) return null;

    const totalSeconds = sequenceDuration(sequence, bpm, reps);
    const elapsedSeconds = ((cycle * steps + Math.max(0, activeStep)) * 60) / bpm;
    const overallProgress = totalSeconds ? (elapsedSeconds / totalSeconds) * 100 : 0;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="full" motionPreset="none">
            <ModalOverlay/>
            <ModalContent bgGradient="linear(to-br, #12100e, #2b1055, #7597de)" color="white" m={0} borderRadius={0}>
                <Flex direction="column" h="100vh" px={{base: 4, md: 10}} py={{base: 5, md: 8}}>
                    {/* header */}
                    <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                            <Flex w="44px" h="44px" borderRadius="12px" bg="whiteAlpha.200" align="center" justify="center">
                                <Icon as={sequence.icon || MdGraphicEq} boxSize="24px"/>
                            </Flex>
                            <Box>
                                <HStack>
                                    <Text fontWeight="bold" fontSize="lg" noOfLines={1}>{sequence.name}</Text>
                                    <Badge colorScheme={meta.color} borderRadius="full">{meta.label}</Badge>
                                </HStack>
                                <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>{sequence.description}</Text>
                            </Box>
                        </HStack>
                        <IconButton aria-label="Close player" icon={<MdClose/>} variant="ghost"
                                    color="white" _hover={{bg: "whiteAlpha.200"}} onClick={handleClose}/>
                    </Flex>

                    {/* stage */}
                    <Flex flex="1" direction="column" align="center" justify="center">
                        {finished ? (
                            <VStack spacing={6}>
                                <Icon as={MdGraphicEq} boxSize="72px" color="purple.200"/>
                                <Text fontSize={{base: "3xl", md: "4xl"}} fontWeight="extrabold" textAlign="center">
                                    Cadence Complete! 🥁
                                </Text>
                                <Text color="whiteAlpha.800">{reps} cycles · {clock(totalSeconds)} total</Text>
                                <Button leftIcon={<MdReplay/>} colorScheme="purple" size="lg" onClick={restart}>
                                    Play again
                                </Button>
                            </VStack>
                        ) : (
                            <VStack spacing={{base: 6, md: 10}} w="100%" maxW="760px">
                                {/* big beat counter */}
                                <VStack spacing={1}>
                                    <Text fontSize="xs" letterSpacing="widest" color="whiteAlpha.600" textTransform="uppercase">
                                        Cycle {Math.min(cycle + 1, reps)} of {reps}
                                    </Text>
                                    <Text fontSize={{base: "6xl", md: "8xl"}} fontWeight="extrabold" lineHeight="1">
                                        {activeStep >= 0 ? activeStep + 1 : "—"}
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.700">Beat</Text>
                                </VStack>

                                {/* step-sequencer grid */}
                                <VStack spacing={2} w="100%">
                                    {rows.map((inst) => {
                                        const info = INSTRUMENTS[inst] || {label: inst, color: "gray"};
                                        return (
                                            <HStack key={inst} spacing={{base: 1, md: 2}} w="100%">
                                                <Text w={{base: "70px", md: "96px"}} flexShrink={0} fontSize={{base: "2xs", md: "sm"}}
                                                      color="whiteAlpha.800" textAlign="right" pr={2} noOfLines={1}>
                                                    {info.label}
                                                </Text>
                                                <HStack spacing={{base: 1, md: 2}} flex="1">
                                                    {Array.from({length: steps}).map((_, i) => {
                                                        const value = sequence.pattern[inst]?.[i] || 0;
                                                        const isActive = i === activeStep;
                                                        const filled = value > 0;
                                                        return (
                                                            <Box
                                                                key={i}
                                                                flex="1"
                                                                h={{base: "26px", md: "40px"}}
                                                                borderRadius="8px"
                                                                bg={
                                                                    filled
                                                                        ? `${info.color}.${value === 2 ? "400" : "600"}`
                                                                        : "whiteAlpha.100"
                                                                }
                                                                opacity={filled ? 1 : 0.5}
                                                                border="2px solid"
                                                                borderColor={isActive ? "white" : "transparent"}
                                                                transform={isActive && filled ? "scale(1.12)" : "scale(1)"}
                                                                boxShadow={isActive && filled ? `0 0 16px var(--chakra-colors-${info.color}-400)` : "none"}
                                                                transition="all 0.08s ease"
                                                            />
                                                        );
                                                    })}
                                                </HStack>
                                            </HStack>
                                        );
                                    })}
                                    {/* beat ruler */}
                                    <HStack spacing={{base: 1, md: 2}} w="100%">
                                        <Box w={{base: "70px", md: "96px"}} flexShrink={0}/>
                                        <HStack spacing={{base: 1, md: 2}} flex="1">
                                            {Array.from({length: steps}).map((_, i) => (
                                                <Text key={i} flex="1" textAlign="center" fontSize="2xs"
                                                      color={i === activeStep ? "white" : "whiteAlpha.500"} fontWeight={i === activeStep ? "bold" : "normal"}>
                                                    {i + 1}
                                                </Text>
                                            ))}
                                        </HStack>
                                    </HStack>
                                </VStack>
                            </VStack>
                        )}
                    </Flex>

                    {/* controls */}
                    {!finished && (
                        <VStack spacing={5}>
                            <HStack spacing={{base: 6, md: 10}}>
                                <IconButton aria-label="Restart" icon={<MdReplay/>} isRound size="lg"
                                            variant="ghost" color="white" _hover={{bg: "whiteAlpha.200"}} onClick={restart}/>
                                <IconButton
                                    aria-label={running ? "Pause" : "Play"}
                                    icon={running ? <MdPause/> : <MdPlayArrow/>}
                                    isRound
                                    boxSize="72px"
                                    fontSize="36px"
                                    colorScheme="purple"
                                    onClick={running ? pause : start}
                                />
                                <Box w="48px"/>
                            </HStack>

                            {/* tempo + reps */}
                            <Wrap spacing={6} justify="center" w="100%" maxW="640px">
                                <WrapItem flex="1" minW="240px">
                                    <Box w="100%">
                                        <Flex justify="space-between" mb={1}>
                                            <Text fontSize="sm" color="whiteAlpha.800">Tempo</Text>
                                            <Text fontSize="sm" fontWeight="bold">{bpm} BPM</Text>
                                        </Flex>
                                        <Slider min={MIN_BPM} max={MAX_BPM} step={1} value={bpm} onChange={setBpm} focusThumbOnChange={false}>
                                            <SliderTrack bg="whiteAlpha.300">
                                                <SliderFilledTrack bg="purple.300"/>
                                            </SliderTrack>
                                            <SliderThumb boxSize={5}/>
                                        </Slider>
                                    </Box>
                                </WrapItem>
                                <WrapItem>
                                    <Box>
                                        <Text fontSize="sm" color="whiteAlpha.800" mb={1}>Repetitions</Text>
                                        <HStack>
                                            <IconButton aria-label="Fewer reps" icon={<MdRemove/>} size="sm" variant="outline"
                                                        color="white" _hover={{bg: "whiteAlpha.200"}} borderColor="whiteAlpha.400"
                                                        isDisabled={reps <= 1} onClick={() => setReps((r) => Math.max(1, r - 1))}/>
                                            <Text w="44px" textAlign="center" fontWeight="bold" fontSize="lg">{reps}</Text>
                                            <IconButton aria-label="More reps" icon={<MdAdd/>} size="sm" variant="outline"
                                                        color="white" _hover={{bg: "whiteAlpha.200"}} borderColor="whiteAlpha.400"
                                                        isDisabled={reps >= 50} onClick={() => setReps((r) => Math.min(50, r + 1))}/>
                                        </HStack>
                                    </Box>
                                </WrapItem>
                            </Wrap>

                            <Box w="100%" maxW="640px">
                                <Flex justify="space-between" fontSize="xs" color="whiteAlpha.700" mb={1}>
                                    <Text>{clock(elapsedSeconds)}</Text>
                                    <Text>{formatDuration(totalSeconds)} total</Text>
                                </Flex>
                                <Progress value={overallProgress} size="sm" colorScheme="purple" borderRadius="full" bg="whiteAlpha.200"/>
                            </Box>
                        </VStack>
                    )}
                </Flex>
            </ModalContent>
        </Modal>
    );
}

export default SequencePlayer;
